/**
 * The trading-card-game **battle engine** (PRD #493, Slice 1): a pure,
 * clock-free, command-driven state machine in the duel / dice-wager / Rat-Swarm
 * mould.
 *
 * Like {@link ./duel} and {@link ./diceWager} this is ephemeral *decision* logic
 * that never reads a wall clock, a socket, or the DOM. The caller (a later server
 * slice) owns the acts - receiving network commands, running the 60-second turn
 * clock, moving wager gold, broadcasting the directed battle state. This module
 * owns only the rules: {@link startCardMatch} shuffles two legal decks with a
 * server-fed seeded RNG and deals opening hands; {@link applyCardCommand} takes
 * one command in (play a card, attack, end turn, time out, concede) and returns
 * the effects out (draw, damage, summon, heal, death, settle) plus mutates the
 * match to its next state. All randomness enters through the RNG at match start;
 * every command after that is deterministic, so a match replays from its seed and
 * command log (PRD "Further Notes").
 *
 * Hearthstone-lite ruleset (PRD, with the captain's 100-HP heroes and the
 * {@link ../sim/cardContent.DAMAGE_SCALE} damage rescale): 100-HP heroes, mana
 * 1..10 (+1/turn), five board slots, minions attack once per turn (not the turn
 * summoned, unless Charge), Taunt must be cleared first, actions resolve
 * immediately, 20-card decks. A timeout auto-ends the turn; two consecutive
 * timeouts forfeit. Fatigue (rising self-damage on an empty-deck draw) guarantees
 * every match terminates.
 */
import {
  BOARD_SLOTS,
  FATIGUE_STEP,
  HAND_LIMIT,
  HERO_START_HP,
  MAX_MANA,
  START_MANA,
  TIMEOUTS_TO_FORFEIT,
  cardDef,
  coinCardDef,
  actionTargetDescriptor,
  scriptedEffectTargetDescriptor,
  scriptedEffectText,
  validateDeck,
} from './cardContent';
import type {
  ActionEffect,
  CardAbilities,
  CardKind,
  CardKeyword,
  CardRarity,
  CardTargetDescriptor,
  DeckList,
  ScriptedCardEffect,
  SlayerGroup,
} from './cardContent';

/** Which of the two seats. 0 is the starting player by default. */
export type Seat = 0 | 1;

/** Extra spell damage taken while Reactive Armor trades away elemental resistance. */
export const REACTIVE_ARMOR_SPELL_PENALTY = 3;

/**
 * Default opening-hand sizes `[first player, second player]` (see
 * {@link CardMatchOptions.openingHand} for the tuning history - this is the
 * seat-fairness start-economy knob, gated by the balance harness).
 */
export const DEFAULT_OPENING_HAND: readonly [number, number] = [2, 3];

/** The other seat. */
export function opponentOf(seat: Seat): Seat {
  return seat === 0 ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Runtime card instances and per-seat state.
// ---------------------------------------------------------------------------

/**
 * A card as it lives inside a match: a stable {@link instanceId} (unique within
 * the match, so commands and effects name a card unambiguously across zones) plus
 * the resolved definition and mutable combat runtime. Monster cards carry
 * attack/health; spell and item cards carry an {@link ActionEffect}.
 */
export interface CardInstance {
  instanceId: number;
  cardId: string;
  name: string;
  rarity: CardRarity;
  kind: CardKind;
  cost: number;
  // Monster runtime (0 / undefined for spells and items).
  attack: number;
  /** Current health; a minion dies at <= 0. */
  health: number;
  maxHealth: number;
  keywords: readonly CardKeyword[];
  slayerGroup?: SlayerGroup | null;
  abilities?: CardAbilities;
  /** Fury starts each turn with two attacks; other minions with one. */
  attacksRemaining?: number;
  /** Divine Fury grants the Fury rules for the current turn without changing printed keywords. */
  temporaryFury?: true;
  protectionSpent?: boolean;
  stealthBroken?: boolean;
  /** Paralyze prevents this minion from attacking until damage breaks it or its turn ends. */
  paralyzed?: boolean;
  /** Whether this minion may still attack this turn (false while summoning-sick). */
  canAttack: boolean;
  /** Whether this minion was summoned this turn (readability for the caller/UI). */
  summonedThisTurn: boolean;
  /** Spell or item resolution (undefined for monsters). */
  effect?: ActionEffect;
}

/** One hero's whole state: health, mana, and the three card zones. */
export interface PlayerState {
  seat: Seat;
  heroHealth: number;
  heroMaxHealth: number;
  /** Mana available to spend this turn. */
  mana: number;
  /** The crystal count this turn (grows +1 per own turn, capped at {@link MAX_MANA}). */
  maxMana: number;
  /** Draw pile - drawn from the front. */
  deck: CardInstance[];
  hand: CardInstance[];
  /** Minions in play, at most {@link BOARD_SLOTS}. */
  board: CardInstance[];
  /** Empty-deck draws so far (drives the rising fatigue damage). */
  fatigue: number;
  /** Consecutive timeouts (two forfeits). Reset by a real end-turn or any action. */
  consecutiveTimeouts: number;
  /** Dead minions in death order, used by resurrection effects. */
  diedMinions?: CardInstance[];
  /** Damage remaining on the hero's one-shot Reactive Armor ward. */
  reactiveArmor?: number;
  /** Whether the hero will reflect the next enemy targeted damage spell. */
  magicReflection?: true;
}

/** Match lifecycle: choosing an opening hand, being played, or resolved. */
export type CardMatchPhase = 'mulligan' | 'playing' | 'ended';

/** Private setup state used only until both opening-hand choices are locked. */
export interface CardMulliganState {
  submitted: [boolean, boolean];
  replaced: [number, number];
  startingSeat: Seat;
  coin: boolean;
  /** Pre-rolled hidden ranks let command application remain RNG-free. */
  ranks: Map<number, number>;
}

/** Why a match ended, for the caller's settle/herald. */
export type CardMatchReason = 'lethal' | 'concede' | 'forfeit' | 'draw';

export interface PendingDelayedDamage {
  sourceSeat: Seat;
  target: CardTarget;
  amount: number;
  resolveAtTurnNumber: number;
}

/** A summon spell whose board slot is reserved until its caster's future turn. */
export interface PendingSummon {
  seat: Seat;
  sourceInstanceId: number;
  cardId: string;
  rarity: CardRarity;
  count: number;
  resolveAtTurnNumber: number;
}

/** The whole battle state - ephemeral, never persisted, never in a world snapshot. */
export interface CardMatch {
  players: [PlayerState, PlayerState];
  /** Whose turn it is. */
  turn: Seat;
  /** Total turns begun (turn 1 is the starting player's first turn). */
  turnNumber: number;
  phase: CardMatchPhase;
  /** Present only during the optional simultaneous opening-hand mulligan. */
  mulligan?: CardMulliganState;
  /** Winning seat once ended; null while playing or on a draw. */
  winner: Seat | null;
  /** Why the match ended (undefined while playing). */
  reason?: CardMatchReason;
  /** Monotonic instance-id source, so newly-drawn/summoned cards stay unique. */
  nextInstanceId: number;
  /** Delayed Explosion spells waiting for the caster's next turn. */
  pendingDelayedDamage: PendingDelayedDamage[];
  /** Delayed summon spells; every pending body reserves one board slot. */
  pendingSummons: PendingSummon[];
  /** Serializable PRNG state for random effects resolved after the opening shuffle. */
  randomState: number;
  /** Turn number at whose start the symmetric Blood Oath link expires. */
  bloodOathExpiresAtTurnNumber?: number;
}

// ---------------------------------------------------------------------------
// Commands and effects (the seam the tests and the server drive).
// ---------------------------------------------------------------------------

/** A stable public hero/minion or owner-private graveyard target. */
export type CardTarget =
  | { kind: 'hero'; seat: Seat }
  | { kind: 'minion'; instanceId: number }
  | { kind: 'graveyard'; instanceId: number };

/** The commands a seat can issue. Every one names the acting `seat`. */
export type CardCommand =
  | { kind: 'Mulligan'; seat: Seat; instanceIds: number[] }
  | { kind: 'PlayCard'; seat: Seat; instanceId: number; target?: CardTarget }
  | { kind: 'Attack'; seat: Seat; attackerId: number; target: CardTarget }
  | { kind: 'EndTurn'; seat: Seat }
  | { kind: 'Timeout'; seat: Seat }
  | { kind: 'Concede'; seat: Seat };

/** The effects a command produces, for the caller to broadcast. */
export type CardEffect =
  | { kind: 'draw'; seat: Seat; instanceId: number; cardId: string; rarity: CardRarity }
  | { kind: 'burn'; seat: Seat; instanceId: number; cardId: string; rarity: CardRarity }
  | { kind: 'fatigue'; seat: Seat; amount: number }
  | { kind: 'summon'; seat: Seat; instanceId: number; cardId: string }
  | { kind: 'play-action'; seat: Seat; instanceId: number; cardId: string }
  | { kind: 'return-to-hand'; seat: Seat; instanceId: number; cardId: string }
  | { kind: 'attack'; attackerId: number; target: CardTarget }
  | { kind: 'damage'; target: CardTarget; amount: number }
  | { kind: 'heal'; target: CardTarget; amount: number }
  | { kind: 'buff'; instanceId: number; attack: number; health: number }
  | { kind: 'status'; action: 'apply' | 'clear'; status: 'paralyze'; seat: Seat; instanceId: number }
  | { kind: 'delayed-damage'; action: 'arm' | 'detonate' | 'fizzle'; sourceSeat: Seat; target: CardTarget; amount: number }
  | { kind: 'death'; instanceId: number; seat: Seat; cardId: string }
  | { kind: 'ability'; seat: Seat; instanceId: number; text: string }
  | { kind: 'fizzle'; seat: Seat; instanceId: number; text: string }
  | { kind: 'ward'; action: 'gain' | 'trigger'; seat: Seat; ward: 'reactive-armor' | 'magic-reflection'; amount?: number }
  | { kind: 'mana'; seat: Seat; mana: number; maxMana: number }
  | { kind: 'turn-start'; seat: Seat; turnNumber: number }
  | { kind: 'settle'; winner: Seat | null; reason: CardMatchReason };

/** The result of applying one command: whether it was legal, and the effects it produced. */
export interface CardCommandResult {
  ok: boolean;
  /** Why an illegal command was rejected (the match is left unchanged when `ok` is false). */
  error?: string;
  effects: CardEffect[];
}

// ---------------------------------------------------------------------------
// Match setup.
// ---------------------------------------------------------------------------

/** Tunable knobs for a match (all defaulted to the ruleset). */
export interface CardMatchOptions {
  /** Which seat takes the first turn (default 0). */
  startingSeat?: Seat;
  /**
   * Hero starting/max health (default {@link HERO_START_HP}). A single number sets
   * both heroes; a `[seat0, seat1]` pair sets each hero independently - the seam the
   * PvE **practice** match (PRD #493 Slice 6) uses to hand the AI opponent a lower,
   * tutorial-gentle HP total while the learning player keeps the full 100 HP. An
   * absent value keeps both heroes at the ruleset default byte-for-byte.
   */
  heroHp?: number | [number, number];
  /**
   * Opening-hand sizes `[first, second]` before the first draw. Default
   * {@link DEFAULT_OPENING_HAND} (`[2, 3]`): the second player's one extra
   * cards (plus the Coin) offset going second. The live server layers the
   * optional selective mulligan over these dealt counts.
   *
   * **This default is a tuned start-economy knob (PRD #638 Slice 3, ADR-0331).**
   * The original `[3, 4]` left every reference mirror's starting player winning
   * 56-68% over fixed seeds; content tuning alone could not close a structural
   * tempo lead, so the sanctioned engine lever (opening hand sizes) was widened
   * to `[2, 4]`. It was subsequently narrowed to `[2, 3]` so the second player
   * receives the Coin plus one extra card rather than two. The per-mirror seat
   * gate in `cardBalance.test.ts` is the acceptance test; do not change this pair
   * without re-running it.
   */
  openingHand?: [number, number];
  /**
   * Whether to pause before turn one for a simultaneous selective mulligan.
   * Defaults false for callers replaying the original mulligan-free rules; the
   * live card-duel server enables it explicitly. Replacements are drawn before
   * rejected cards return, so the same instance cannot be redrawn immediately.
   */
  mulligan?: boolean;
  /**
   * Whether the second player is dealt **the Coin** - one zero-cost card whose
   * effect grants one temporary mana for the turn it is played (PRD #638 Slice 2,
   * replacing the old invisible first-turn bonus mana). Default true. Together
   * with the extra opening card this keeps a mirror match's going-first
   * advantage in a fair band (validated by the balance harness); set false to
   * measure the raw first-player edge.
   */
  coin?: boolean;
}

/**
 * Shuffle in place with a Fisher-Yates over the server-fed RNG.
 */
function shuffle<T>(arr: T[], rng: () => number): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

/** Advance the match-local LCG and return one unbiased-enough index for a small content pool. */
function nextRandomIndex(match: CardMatch, length: number): number {
  match.randomState = (Math.imul(match.randomState, 1_664_525) + 1_013_904_223) >>> 0;
  return Math.floor((match.randomState / 0x1_0000_0000) * length);
}

/** Turn a deck list into runtime instances (unique ids from a shared counter). */
function instantiate(deck: DeckList, next: () => number): CardInstance[] {
  return deck.map((ref) => {
    const def = cardDef(ref.cardId, ref.rarity);
    const base: CardInstance = {
      instanceId: next(),
      cardId: def.cardId,
      name: def.name,
      rarity: def.rarity,
      kind: def.kind,
      cost: def.cost,
      attack: def.kind === 'monster' ? def.attack : 0,
      health: def.kind === 'monster' ? def.health : 0,
      maxHealth: def.kind === 'monster' ? def.health : 0,
      keywords: def.kind === 'monster' ? def.keywords : [],
      slayerGroup: def.kind === 'monster' ? def.slayerGroup : undefined,
      abilities: def.kind === 'monster' ? def.abilities : undefined,
      attacksRemaining: 0,
      protectionSpent: false,
      stealthBroken: false,
      canAttack: false,
      summonedThisTurn: false,
    };
    if (def.kind !== 'monster') base.effect = def.effect;
    return base;
  });
}

/**
 * Build the second player's Coin (PRD #638 Slice 2): a match-transient runtime
 * instance of {@link coinCardDef}. Dealt exactly once by {@link startCardMatch};
 * nothing else in the engine or content can create one (the Coin is not in any
 * released set, so {@link cardDef} - the path every summon/draw goes through -
 * throws on its id).
 */
function makeCoinInstance(instanceId: number): CardInstance {
  const def = coinCardDef();
  return {
    instanceId,
    cardId: def.cardId,
    name: def.name,
    rarity: def.rarity,
    kind: def.kind,
    cost: def.cost,
    attack: 0,
    health: 0,
    maxHealth: 0,
    keywords: [],
    attacksRemaining: 0,
    protectionSpent: false,
    stealthBroken: false,
    canAttack: false,
    summonedThisTurn: false,
    effect: def.effect,
  };
}

/**
 * Start a match from two decks and a seeded RNG. Both decks are validated (an
 * illegal deck throws - deck legality is enforced at this seam), shuffled, and
 * dealt opening hands; then either pauses for the optional mulligan or begins
 * the starting seat's first turn (mana to 1, a draw). The returned
 * {@link CardMatch} is ready for {@link applyCardCommand}.
 */
export function startCardMatch(
  deckA: DeckList,
  deckB: DeckList,
  rng: () => number,
  opts: CardMatchOptions = {},
): CardMatch {
  const a = validateDeck(deckA);
  if (!a.ok) throw new Error(`seat 0 deck illegal: ${a.error}`);
  const b = validateDeck(deckB);
  if (!b.ok) throw new Error(`seat 1 deck illegal: ${b.error}`);

  const heroHpFor = (seat: Seat): number =>
    Array.isArray(opts.heroHp) ? opts.heroHp[seat] : opts.heroHp ?? HERO_START_HP;
  const startingSeat: Seat = opts.startingSeat ?? 0;
  const [firstHand, secondHand] = opts.openingHand ?? DEFAULT_OPENING_HAND;

  let idCounter = 1;
  const next = () => idCounter++;

  const deck0 = instantiate(deckA, next);
  const deck1 = instantiate(deckB, next);
  shuffle(deck0, rng);
  shuffle(deck1, rng);

  const makePlayer = (seat: Seat, deck: CardInstance[]): PlayerState => ({
    seat,
    heroHealth: heroHpFor(seat),
    heroMaxHealth: heroHpFor(seat),
    mana: 0,
    maxMana: START_MANA - 1, // beginTurn adds one, landing the first turn on START_MANA
    deck,
    hand: [],
    board: [],
    fatigue: 0,
    consecutiveTimeouts: 0,
    diedMinions: [],
  });

  const match: CardMatch = {
    players: [makePlayer(0, deck0), makePlayer(1, deck1)],
    turn: startingSeat,
    turnNumber: 0,
    phase: opts.mulligan ? 'mulligan' : 'playing',
    winner: null,
    nextInstanceId: idCounter,
    pendingDelayedDamage: [],
    pendingSummons: [],
    randomState: 0,
  };

  // Deal opening hands off the top (no draw effect - these precede the optional
  // mulligan and are not a turn draw). The starting seat gets `firstHand`.
  const handSizes: [number, number] = startingSeat === 0 ? [firstHand, secondHand] : [secondHand, firstHand];
  for (const seat of [0, 1] as Seat[]) {
    const p = match.players[seat];
    for (let i = 0; i < handSizes[seat] && p.deck.length > 0; i++) p.hand.push(p.deck.shift()!);
  }

  if (opts.mulligan) {
    const ranks = new Map<number, number>();
    for (const card of [...match.players[0].deck, ...match.players[0].hand,
      ...match.players[1].deck, ...match.players[1].hand]) {
      ranks.set(card.instanceId, rng());
    }
    match.mulligan = {
      submitted: [false, false],
      replaced: [0, 0],
      startingSeat,
      coin: opts.coin ?? true,
      ranks,
    };
    // Preserve the established shuffle/mulligan draw order, then seed later random
    // card effects from the same caller-owned deterministic stream.
    match.randomState = Math.floor(rng() * 0x1_0000_0000) >>> 0;
    match.nextInstanceId = idCounter;
    return match;
  }

  match.randomState = Math.floor(rng() * 0x1_0000_0000) >>> 0;

  // The second player is dealt the Coin: a holdable zero-cost card worth one
  // temporary mana on the turn it is played (never automatic, never the cap).
  if (opts.coin ?? true) {
    match.players[opponentOf(startingSeat)].hand.push(makeCoinInstance(next()));
  }

  // Begin the starting player's first turn (mana -> START_MANA, a draw).
  beginTurn(match, startingSeat, []);
  match.nextInstanceId = idCounter;
  return match;
}

// ---------------------------------------------------------------------------
// Internal mutators (all pure of clock/DOM; effects pushed onto `effects`).
// ---------------------------------------------------------------------------

/** Begin `seat`'s turn: grow mana, refill, ready minions, draw one. */
function beginTurn(match: CardMatch, seat: Seat, effects: CardEffect[]): void {
  const p = match.players[seat];
  match.turn = seat;
  match.turnNumber++;
  if (
    match.bloodOathExpiresAtTurnNumber !== undefined
    && match.turnNumber >= match.bloodOathExpiresAtTurnNumber
  ) match.bloodOathExpiresAtTurnNumber = undefined;
  effects.push({ kind: 'turn-start', seat, turnNumber: match.turnNumber });
  resolveDueDelayedDamage(match, effects);
  if (match.phase === 'ended') return;
  // The refill is exactly the crystal count: any temporary mana (the Coin)
  // left unspent last turn has already expired.
  p.maxMana = Math.min(MAX_MANA, p.maxMana + 1);
  p.mana = p.maxMana;
  for (const m of p.board) {
    m.canAttack = !m.paralyzed;
    m.attacksRemaining = m.paralyzed ? 0 : (m.keywords.includes('fury') ? 2 : 1);
    m.summonedThisTurn = false;
  }
  resolveDueSummons(match, seat, effects);
  effects.push({ kind: 'mana', seat, mana: p.mana, maxMana: p.maxMana });
  drawCard(match, seat, effects);
}

/** Board capacity remaining after both live minions and delayed reservations. */
export function openBoardSlots(match: CardMatch, seat: Seat): number {
  const reserved = match.pendingSummons
    .filter((pending) => pending.seat === seat)
    .reduce((sum, pending) => sum + pending.count, 0);
  return Math.max(0, BOARD_SLOTS - match.players[seat].board.length - reserved);
}

export function hasOpenBoardSlot(match: CardMatch, seat: Seat): boolean {
  return openBoardSlots(match, seat) > 0;
}

/** Materialize every reserved summon due at the start of `seat`'s turn. */
function resolveDueSummons(match: CardMatch, seat: Seat, effects: CardEffect[]): void {
  const due = match.pendingSummons.filter((pending) =>
    pending.seat === seat && pending.resolveAtTurnNumber <= match.turnNumber,
  );
  match.pendingSummons = match.pendingSummons.filter((pending) => !due.includes(pending));
  for (const pending of due) {
    for (let i = 0; i < pending.count && hasOpenBoardSlot(match, seat); i++) {
      const def = cardDef(pending.cardId, pending.rarity);
      if (def.kind !== 'monster') break;
      const summoned: CardInstance = {
        instanceId: match.nextInstanceId++,
        cardId: def.cardId,
        name: def.name,
        rarity: def.rarity,
        kind: 'monster',
        cost: def.cost,
        attack: def.attack,
        health: def.health,
        maxHealth: def.health,
        keywords: def.keywords,
        slayerGroup: def.slayerGroup,
        abilities: def.abilities,
        // A delayed cast controls when the body materializes; it does not waive
        // the ordinary summoning-sickness turn once that body is on the board.
        canAttack: def.keywords.includes('charge'),
        summonedThisTurn: true,
        attacksRemaining: def.keywords.includes('charge')
          ? (def.keywords.includes('fury') ? 2 : 1)
          : 0,
      };
      match.players[seat].board.push(summoned);
      effects.push({ kind: 'summon', seat, instanceId: summoned.instanceId, cardId: summoned.cardId });
    }
  }
}

/** Detonate every Explosion whose caster has reached their next turn. */
function resolveDueDelayedDamage(match: CardMatch, effects: CardEffect[]): void {
  const due = match.pendingDelayedDamage.filter((pending) =>
    pending.resolveAtTurnNumber <= match.turnNumber,
  );
  match.pendingDelayedDamage = match.pendingDelayedDamage.filter((pending) =>
    pending.resolveAtTurnNumber > match.turnNumber,
  );
  for (const pending of due) {
    if (
      pending.target.kind === 'minion'
      && !findMinion(match, pending.target.instanceId)
    ) {
      effects.push({
        kind: 'delayed-damage',
        action: 'fizzle',
        sourceSeat: pending.sourceSeat,
        target: pending.target,
        amount: pending.amount,
      });
      continue;
    }
    effects.push({
      kind: 'delayed-damage',
      action: 'detonate',
      sourceSeat: pending.sourceSeat,
      target: pending.target,
      amount: pending.amount,
    });
    damageSpellTarget(match, pending.sourceSeat, pending.target, pending.amount, effects);
    if (match.phase === 'ended') return;
  }
}

/** Draw one card for `seat`, taking fatigue on an empty deck and burning over the hand cap. */
function drawCard(match: CardMatch, seat: Seat, effects: CardEffect[]): void {
  const p = match.players[seat];
  if (p.deck.length === 0) {
    p.fatigue += 1;
    const amount = p.fatigue * FATIGUE_STEP;
    effects.push({ kind: 'fatigue', seat, amount });
    damageHero(match, seat, amount, 'fatigue', effects);
    return;
  }
  const card = p.deck.shift()!;
  if (p.hand.length >= HAND_LIMIT) {
    effects.push({
      kind: 'burn',
      seat,
      instanceId: card.instanceId,
      cardId: card.cardId,
      rarity: card.rarity,
    });
    return;
  }
  p.hand.push(card);
  effects.push({ kind: 'draw', seat, instanceId: card.instanceId, cardId: card.cardId, rarity: card.rarity });
}

type HeroDamageKind = 'attack' | 'spell' | 'ability' | 'fatigue';

/** Deal `amount` to `seat`'s hero and re-evaluate the win condition. */
function damageHero(
  match: CardMatch,
  seat: Seat,
  amount: number,
  kind: HeroDamageKind,
  effects: CardEffect[],
): number {
  const p = match.players[seat];
  const adjustedAmount =
    kind === 'spell' && p.reactiveArmor !== undefined
      ? amount + REACTIVE_ARMOR_SPELL_PENALTY
      : amount;
  const absorbed = kind === 'attack' ? Math.min(adjustedAmount, p.reactiveArmor ?? 0) : 0;
  if (absorbed > 0) {
    p.reactiveArmor = (p.reactiveArmor ?? 0) - absorbed;
    if (p.reactiveArmor === 0) p.reactiveArmor = undefined;
    effects.push({ kind: 'ward', action: 'trigger', seat, ward: 'reactive-armor', amount: absorbed });
  }
  const dealt = adjustedAmount - absorbed;
  p.heroHealth -= dealt;
  if (dealt > 0) effects.push({ kind: 'damage', target: { kind: 'hero', seat }, amount: dealt });
  // Blood Oath mirrors the resolved amount exactly once. Mutating the opposite
  // hero directly is intentional: routing back through damageHero would recurse,
  // and the mirrored loss is neither an attack nor a spell for ward purposes.
  if (dealt > 0 && match.bloodOathExpiresAtTurnNumber !== undefined) {
    const mirroredSeat = opponentOf(seat);
    match.players[mirroredSeat].heroHealth -= dealt;
    effects.push({ kind: 'damage', target: { kind: 'hero', seat: mirroredSeat }, amount: dealt });
  }
  checkWin(match, effects);
  return dealt;
}

type PendingDeath = { minion: CardInstance; seat: Seat };

/**
 * Remove dead minions and resolve their triggers through one breadth-first queue.
 * A chained death joins the back of the active queue, so every trigger from an
 * earlier simultaneous batch resolves before the newly caused death trigger.
 */
function reap(match: CardMatch, effects: CardEffect[], activeQueue?: PendingDeath[]): void {
  const deaths = activeQueue ?? [];
  for (const seat of [0, 1] as Seat[]) {
    const p = match.players[seat];
    const survivors: CardInstance[] = [];
    for (const m of p.board) {
      if (m.health <= 0) {
        effects.push({ kind: 'death', instanceId: m.instanceId, seat, cardId: m.cardId });
        (p.diedMinions ??= []).push({ ...m });
        deaths.push({ minion: m, seat });
      }
      else survivors.push(m);
    }
    p.board = survivors;
  }
  if (activeQueue) return;
  for (let cursor = 0; cursor < deaths.length; cursor++) {
    const death = deaths[cursor]!;
    if (death.minion.abilities?.onDeath) {
      resolveScriptedEffect(
        match,
        death.seat,
        death.minion,
        death.minion.abilities.onDeath,
        effects,
        undefined,
        deaths,
      );
    }
  }
}

function damageMinion(match: CardMatch, minion: CardInstance, amount: number, effects: CardEffect[]): number {
  if (minion.keywords.includes('protection') && !minion.protectionSpent) {
    minion.protectionSpent = true;
    effects.push({ kind: 'ability', seat: findMinion(match, minion.instanceId)?.seat ?? 0, instanceId: minion.instanceId, text: 'Protection prevents the damage.' });
    return 0;
  }
  minion.health -= amount;
  effects.push({ kind: 'damage', target: { kind: 'minion', instanceId: minion.instanceId }, amount });
  if (amount > 0 && minion.paralyzed) clearParalyze(match, minion, effects);
  return amount;
}

/**
 * The heal a hero can actually take: up to maximum health, never negative.
 * A hero can sit above the maximum (`gain-hero-health` explicitly exceeds it),
 * and healing an already full or overfull hero is a no-op - it must never pull
 * banked over-maximum health back down to the cap.
 */
function heroHealAmount(player: PlayerState, amount: number): number {
  return Math.max(0, Math.min(amount, player.heroMaxHealth - player.heroHealth));
}

function healHeroFromDrain(
  match: CardMatch,
  seat: Seat,
  dealt: number,
  effects: CardEffect[],
): void {
  const player = match.players[seat];
  const healed = heroHealAmount(player, dealt);
  player.heroHealth += healed;
  effects.push({ kind: 'heal', target: { kind: 'hero', seat }, amount: healed });
}

/** Resolve one targeted damage spell, including Magic Reflection on hero targets. */
function damageSpellTarget(
  match: CardMatch,
  seat: Seat,
  target: CardTarget,
  amount: number,
  effects: CardEffect[],
): void {
  if (target.kind === 'hero') {
    const reflected = target.seat !== seat && match.players[target.seat].magicReflection;
    if (reflected) {
      match.players[target.seat].magicReflection = undefined;
      effects.push({ kind: 'ward', action: 'trigger', seat: target.seat, ward: 'magic-reflection' });
      damageHero(match, seat, amount, 'spell', effects);
    } else {
      damageHero(match, target.seat, amount, 'spell', effects);
    }
    return;
  }
  const hit = findMinion(match, target.instanceId);
  if (!hit) return;
  damageMinion(match, hit.minion, amount, effects);
  reap(match, effects);
}

/** Damage, a trapped pouch, or natural expiry releases one paralyzed minion. */
function clearParalyze(match: CardMatch, minion: CardInstance, effects: CardEffect[]): void {
  if (!minion.paralyzed) return;
  minion.paralyzed = false;
  const found = findMinion(match, minion.instanceId);
  if (!found) return;
  if (match.turn === found.seat && !minion.summonedThisTurn) {
    minion.canAttack = true;
    minion.attacksRemaining = minion.keywords.includes('fury') ? 2 : 1;
  }
  effects.push({
    kind: 'status',
    action: 'clear',
    status: 'paralyze',
    seat: found.seat,
    instanceId: minion.instanceId,
  });
}

/**
 * The scripted-effect kinds that count as a **hostile action** for the Stealth
 * rule (PRD #638): producing hostile damage (or destruction) reveals the
 * source, exactly like attacking. Any future authored effect that harms the
 * enemy belongs in this set.
 */
const HOSTILE_SCRIPTED_EFFECT_KINDS: ReadonlySet<ScriptedCardEffect['kind']> = new Set([
  'damage-enemy-hero',
  'damage-all-enemies',
  'destroy-low-attack',
]);

/**
 * Break the Stealth of a **live** minion whose authored trigger produced
 * hostile damage (PRD #638: Stealth breaks when the minion performs a hostile
 * action, whether an attack or a trigger). Emitted as a battle-log `ability`
 * effect in the same command resolution, so the break is externally visible
 * before the opponent's next opportunity to target the minion. A dead source
 * (an onDeath trigger) has already left play, so there is nothing to reveal;
 * receiving damage never routes through here, so it never breaks Stealth.
 */
function breakStealthOnHostileTrigger(
  match: CardMatch,
  seat: Seat,
  source: CardInstance,
  effects: CardEffect[],
): void {
  if (!source.keywords.includes('stealth') || source.stealthBroken) return;
  const live = findMinion(match, source.instanceId);
  if (!live || live.minion !== source) return;
  source.stealthBroken = true;
  effects.push({
    kind: 'ability',
    seat,
    instanceId: source.instanceId,
    text: `${source.name} is revealed - its hostile strike breaks Stealth.`,
  });
}

function resolveScriptedEffect(
  match: CardMatch,
  seat: Seat,
  source: CardInstance,
  effect: ScriptedCardEffect,
  effects: CardEffect[],
  selectedTarget?: CardTarget,
  deathQueue?: PendingDeath[],
): void {
  effects.push({ kind: 'ability', seat, instanceId: source.instanceId, text: `${source.name}: ${scriptedEffectText(effect)}.` });
  applyScriptedEffectBody(match, seat, source, effect, effects, selectedTarget, deathQueue);
  // Stealth breaks *after* the minion's hostile action resolves (PRD #638), so it
  // is revealed by its own strike landing - never exposed a beat early (e.g. the
  // Ghoul stays hidden until its end-of-turn damage has been dealt). A source that
  // died resolving the effect (an onDeath trigger) is already gone, so
  // breakStealthOnHostileTrigger no-ops and reveals nothing.
  if (HOSTILE_SCRIPTED_EFFECT_KINDS.has(effect.kind)) {
    breakStealthOnHostileTrigger(match, seat, source, effects);
  }
}

function applyScriptedEffectBody(
  match: CardMatch,
  seat: Seat,
  source: CardInstance,
  effect: ScriptedCardEffect,
  effects: CardEffect[],
  selectedTarget?: CardTarget,
  deathQueue?: PendingDeath[],
): void {
  const self = match.players[seat];
  const foe = match.players[opponentOf(seat)];
  switch (effect.kind) {
    case 'damage-enemy-hero': damageHero(match, opponentOf(seat), effect.amount, 'ability', effects); return;
    case 'damage-all-enemies': for (const m of foe.board) damageMinion(match, m, effect.amount, effects); reap(match, effects, deathQueue); return;
    case 'heal-hero': {
      const healed = heroHealAmount(self, effect.amount); self.heroHealth += healed;
      effects.push({ kind: 'heal', target: { kind: 'hero', seat }, amount: healed }); return;
    }
    case 'gain-hero-health': {
      self.heroHealth += effect.amount;
      effects.push({ kind: 'heal', target: { kind: 'hero', seat }, amount: effect.amount }); return;
    }
    case 'magic-reflection': {
      self.magicReflection = true;
      effects.push({ kind: 'ward', action: 'gain', seat, ward: 'magic-reflection' }); return;
    }
    case 'draw': for (let i = 0; i < effect.amount; i++) drawCard(match, seat, effects); return;
    case 'buff-other-slayer-group': for (const m of self.board) if (m !== source && m.slayerGroup === effect.slayerGroup) {
      m.attack += effect.attack; m.health += effect.health; m.maxHealth += effect.health;
      effects.push({ kind: 'buff', instanceId: m.instanceId, attack: effect.attack, health: effect.health });
    } return;
    case 'destroy-low-attack': {
      // On-play uses the accepted player selection. Triggered effects never
      // pause for input and retain deterministic strongest-valid resolution.
      const target = selectedTarget?.kind === 'minion'
        ? foe.board.find((m) => m.instanceId === selectedTarget.instanceId)
        : foe.board.filter((m) => m.attack <= effect.maximumAttack).sort((a, b) => b.attack - a.attack)[0];
      if (target) { target.health = 0; reap(match, effects, deathQueue); } return;
    }
    case 'resurrect': {
      // A death-triggered resurrect must never select the source instance that
      // is currently resolving the trigger (the "immortal skeleton" exploit,
      // PRD #638): its own corpse is already in the graveyard by the time the
      // trigger runs, so skip it and take the most recent earlier valid corpse.
      const grave = self.diedMinions ?? [];
      let index = selectedTarget?.kind === 'graveyard'
        ? grave.findIndex((corpse) => corpse.instanceId === selectedTarget.instanceId)
        : -1;
      if (index < 0 && selectedTarget === undefined) {
        for (let i = grave.length - 1; i >= 0; i--) {
          const corpse = grave[i]!;
          if (corpse.instanceId === source.instanceId) continue;
          if (!effect.slayerGroup || corpse.slayerGroup === effect.slayerGroup) { index = i; break; }
        }
      }
      if (index < 0) {
        effects.push({ kind: 'fizzle', seat, instanceId: source.instanceId, text: `${source.name} has no valid fallen minion to return.` });
        return;
      }
      if (!hasOpenBoardSlot(match, seat)) {
        // Fizzle without consuming the corpse - the graveyard is untouched.
        effects.push({ kind: 'fizzle', seat, instanceId: source.instanceId, text: `${source.name} has no room to return a fallen minion.` });
        return;
      }
      // Resurrected minions come back as **fresh printed instances** at full
      // printed health: temporary buffs, spent Protection, broken Stealth,
      // Paralyze, and all other runtime state reset to the printed card.
      const corpse = grave.splice(index, 1)[0]!;
      const def = cardDef(corpse.cardId, corpse.rarity);
      if (def.kind !== 'monster') return;
      const revived: CardInstance = {
        instanceId: match.nextInstanceId++,
        cardId: def.cardId,
        name: def.name,
        rarity: def.rarity,
        kind: 'monster',
        cost: def.cost,
        attack: def.attack,
        health: def.health,
        maxHealth: def.health,
        keywords: def.keywords,
        slayerGroup: def.slayerGroup,
        abilities: def.abilities,
        // A revived minion honours the printed card's keywords the same way a
        // freshly-summoned copy does: Haste (Charge) waives summoning sickness so
        // it can strike at once (twice with Fury), otherwise it is summoning-sick.
        attacksRemaining: def.keywords.includes('charge')
          ? (def.keywords.includes('fury') ? 2 : 1)
          : 0,
        protectionSpent: false,
        stealthBroken: false,
        paralyzed: false,
        canAttack: def.keywords.includes('charge'),
        summonedThisTurn: true,
      };
      self.board.push(revived);
      effects.push({ kind: 'summon', seat, instanceId: revived.instanceId, cardId: revived.cardId });
      return;
    }
    case 'summon': {
      let summonedCount = 0;
      for (let i = 0; i < (effect.count ?? 1) && hasOpenBoardSlot(match, seat); i++) {
        const def = cardDef(effect.cardId, source.rarity);
        if (def.kind !== 'monster') break;
        const summoned: CardInstance = { instanceId: match.nextInstanceId++, cardId: def.cardId, name: def.name, rarity: def.rarity, kind: 'monster', cost: def.cost, attack: def.attack, health: def.health, maxHealth: def.health, keywords: def.keywords, slayerGroup: def.slayerGroup, abilities: def.abilities, canAttack: def.keywords.includes('charge'), summonedThisTurn: true, attacksRemaining: def.keywords.includes('charge') ? (def.keywords.includes('fury') ? 2 : 1) : 0 };
        self.board.push(summoned);
        effects.push({ kind: 'summon', seat, instanceId: summoned.instanceId, cardId: summoned.cardId });
        summonedCount += 1;
      }
      if (summonedCount < (effect.count ?? 1)) {
        effects.push({ kind: 'fizzle', seat, instanceId: source.instanceId, text: `${source.name} has no room for ${summonedCount === 0 ? 'a summon' : 'another summon'}.` });
      }
      return;
    }
  }
}

/** End the match if either hero is down. Both down is a draw. Idempotent. */
function checkWin(match: CardMatch, effects: CardEffect[]): void {
  if (match.phase === 'ended') return;
  const dead0 = match.players[0].heroHealth <= 0;
  const dead1 = match.players[1].heroHealth <= 0;
  if (!dead0 && !dead1) return;
  match.phase = 'ended';
  if (dead0 && dead1) {
    match.winner = null;
    match.reason = 'draw';
  } else {
    match.winner = dead0 ? 1 : 0;
    match.reason = 'lethal';
  }
  effects.push({ kind: 'settle', winner: match.winner, reason: match.reason! });
}

/** End the match with an explicit winner/reason (concede, forfeit). Idempotent. */
function settle(match: CardMatch, winner: Seat | null, reason: CardMatchReason, effects: CardEffect[]): void {
  if (match.phase === 'ended') return;
  match.phase = 'ended';
  match.winner = winner;
  match.reason = reason;
  effects.push({ kind: 'settle', winner, reason });
}

// ---------------------------------------------------------------------------
// Command handlers.
// ---------------------------------------------------------------------------

const reject = (error: string): CardCommandResult => ({ ok: false, error, effects: [] });

/** Apply one command, mutating the match and returning its effects (or a rejection). */
export function applyCardCommand(match: CardMatch, cmd: CardCommand): CardCommandResult {
  if (match.phase === 'ended') return reject('match has ended');
  if (match.phase === 'mulligan') {
    if (cmd.kind === 'Mulligan') return applyMulligan(match, cmd.seat, cmd.instanceIds);
    if (cmd.kind === 'Concede') return concede(match, cmd.seat);
    return reject('mulligan is in progress');
  }
  switch (cmd.kind) {
    case 'Mulligan':
      return reject('mulligan has ended');
    case 'PlayCard':
      return playCard(match, cmd);
    case 'Attack':
      return attack(match, cmd);
    case 'EndTurn':
      return endTurn(match, cmd.seat, false);
    case 'Timeout':
      return endTurn(match, cmd.seat, true);
    case 'Concede':
      return concede(match, cmd.seat);
  }
}

/** Lock one seat's opening choice and begin turn one once both seats have locked. */
function applyMulligan(match: CardMatch, seat: Seat, instanceIds: number[]): CardCommandResult {
  const setup = match.mulligan!;
  if (setup.submitted[seat]) return reject('mulligan already submitted');
  if (new Set(instanceIds).size !== instanceIds.length) return reject('duplicate mulligan card');

  const player = match.players[seat];
  const selected = new Set(instanceIds);
  if (instanceIds.some((id) => !player.hand.some((card) => card.instanceId === id))) {
    return reject('card not in opening hand');
  }

  const rejected = player.hand.filter((card) => selected.has(card.instanceId));
  player.hand = player.hand.filter((card) => !selected.has(card.instanceId));
  for (let i = 0; i < rejected.length && player.deck.length > 0; i++) {
    player.hand.push(player.deck.shift()!);
  }
  player.deck.push(...rejected);
  if (rejected.length > 0) {
    player.deck.sort((a, b) => setup.ranks.get(a.instanceId)! - setup.ranks.get(b.instanceId)!);
  }
  setup.submitted[seat] = true;
  setup.replaced[seat] = rejected.length;

  const effects: CardEffect[] = [];
  if (setup.submitted[0] && setup.submitted[1]) {
    if (setup.coin) {
      match.players[opponentOf(setup.startingSeat)].hand.push(makeCoinInstance(match.nextInstanceId++));
    }
    const startingSeat = setup.startingSeat;
    match.phase = 'playing';
    delete match.mulligan;
    beginTurn(match, startingSeat, effects);
  }
  return { ok: true, effects };
}

/** Find a minion on either board by instance id. */
function findMinion(match: CardMatch, instanceId: number): { minion: CardInstance; seat: Seat } | undefined {
  for (const seat of [0, 1] as Seat[]) {
    const minion = match.players[seat].board.find((m) => m.instanceId === instanceId);
    if (minion) return { minion, seat };
  }
  return undefined;
}

function playCard(match: CardMatch, cmd: Extract<CardCommand, { kind: 'PlayCard' }>): CardCommandResult {
  if (cmd.seat !== match.turn) return reject('not your turn');
  const p = match.players[cmd.seat];
  const idx = p.hand.findIndex((c) => c.instanceId === cmd.instanceId);
  if (idx === -1) return reject('card not in hand');
  const card = p.hand[idx]!;
  if (card.cost > p.mana) return reject('not enough mana');

  if (card.kind === 'monster') {
    if (!hasOpenBoardSlot(match, cmd.seat)) return reject('board is full');
  }

  // Every player-authored target is validated before mana, hand, board, or
  // graveyard mutation. This includes a monster's targeted on-play ability.
  const targetError = validateCardTarget(match, cmd.seat, card, cmd.target);
  if (targetError) return reject(targetError);

  const effects: CardEffect[] = [];
  // Playing anything counts as acting, so a stalling forfeit only comes from
  // literal consecutive timeouts, never from a turn that did real work.
  p.consecutiveTimeouts = 0;

  if (card.kind === 'monster') {
    p.hand.splice(idx, 1);
    p.mana -= card.cost;
    card.summonedThisTurn = true;
    card.canAttack = card.keywords.includes('charge');
    card.attacksRemaining = card.canAttack ? (card.keywords.includes('fury') ? 2 : 1) : 0;
    p.board.push(card);
    effects.push({ kind: 'summon', seat: cmd.seat, instanceId: card.instanceId, cardId: card.cardId });
    if (card.abilities?.onPlay) {
      resolveScriptedEffect(match, cmd.seat, card, card.abilities.onPlay, effects, cmd.target);
    }
    effects.push({ kind: 'mana', seat: cmd.seat, mana: p.mana, maxMana: p.maxMana });
    return { ok: true, effects };
  }

  p.hand.splice(idx, 1);
  p.mana -= card.cost;
  effects.push({ kind: 'play-action', seat: cmd.seat, instanceId: card.instanceId, cardId: card.cardId });
  resolveAction(match, cmd.seat, card, cmd.target, effects);
  effects.push({ kind: 'mana', seat: cmd.seat, mana: p.mana, maxMana: p.maxMana });
  return { ok: true, effects };
}

/** One card's authored target descriptor (the single validation/UI/AI seam). */
export function cardTargetDescriptor(card: CardInstance): CardTargetDescriptor {
  if (card.kind !== 'monster') return actionTargetDescriptor(card.effect!);
  return card.abilities?.onPlay
    ? scriptedEffectTargetDescriptor(card.abilities.onPlay, 'onPlay')
    : { kind: 'none' };
}

function targetMatchesDescriptor(
  match: CardMatch,
  seat: Seat,
  descriptor: CardTargetDescriptor,
  target: CardTarget,
): boolean {
  const foe = opponentOf(seat);
  if (target.kind === 'hero') {
    return (descriptor.kind === 'friendly-hero' && target.seat === seat)
      || (descriptor.kind === 'enemy-hero' && target.seat === foe)
      || descriptor.kind === 'any-hero'
      || (descriptor.kind === 'friendly-character' && target.seat === seat)
      || (descriptor.kind === 'enemy-character' && target.seat === foe)
      || descriptor.kind === 'any-character';
  }
  if (target.kind === 'graveyard') {
    const corpse = match.players[seat].diedMinions?.find((c) => c.instanceId === target.instanceId);
    return descriptor.kind === 'friendly-graveyard-minion'
      && corpse !== undefined
      && (!descriptor.slayerGroup || corpse.slayerGroup === descriptor.slayerGroup);
  }
  const hit = findMinion(match, target.instanceId);
  if (!hit) return false;
  const friendly = hit.seat === seat;
  const classMatches =
    descriptor.kind === 'any-minion'
    || descriptor.kind === 'any-character'
    || (friendly && (descriptor.kind === 'friendly-minion' || descriptor.kind === 'friendly-character'))
    || (!friendly && (descriptor.kind === 'enemy-minion' || descriptor.kind === 'enemy-character'));
  if (!classMatches) return false;
  if (!friendly && hit.minion.keywords.includes('stealth') && !hit.minion.stealthBroken) return false;
  if (descriptor.maximumAttack !== undefined && hit.minion.attack > descriptor.maximumAttack) return false;
  if (descriptor.slayerGroup !== undefined && hit.minion.slayerGroup !== descriptor.slayerGroup) return false;
  if (descriptor.damaged !== undefined && (hit.minion.health < hit.minion.maxHealth) !== descriptor.damaged) return false;
  if (descriptor.paralyzed !== undefined && Boolean(hit.minion.paralyzed) !== descriptor.paralyzed) return false;
  return true;
}

/** Legal live candidates for a card, in stable hero/board/death order. */
export function cardTargetCandidates(match: CardMatch, seat: Seat, card: CardInstance): CardTarget[] {
  const descriptor = cardTargetDescriptor(card);
  if (descriptor.kind === 'none') return [];
  const all: CardTarget[] = [
    { kind: 'hero', seat },
    { kind: 'hero', seat: opponentOf(seat) },
    ...match.players[seat].board.map((m): CardTarget => ({ kind: 'minion', instanceId: m.instanceId })),
    ...match.players[opponentOf(seat)].board.map((m): CardTarget => ({ kind: 'minion', instanceId: m.instanceId })),
    ...(match.players[seat].diedMinions ?? []).map((m): CardTarget => ({
      kind: 'graveyard',
      instanceId: m.instanceId,
    })),
  ];
  return all.filter((target) => targetMatchesDescriptor(match, seat, descriptor, target));
}

/** Validate one selected target without mutating the match. */
export function validateCardTarget(
  match: CardMatch,
  seat: Seat,
  card: CardInstance,
  target: CardTarget | undefined,
): string | null {
  if (card.effect?.kind === 'summon' && card.effect.summonDelayTurns && !hasOpenBoardSlot(match, seat)) {
    return 'board is full';
  }
  const descriptor = cardTargetDescriptor(card);
  if (
    descriptor.kind === 'friendly-graveyard-minion'
    && !hasOpenBoardSlot(match, seat)
  ) return 'board is full';
  if (descriptor.kind === 'none') return target === undefined ? null : 'card does not take a target';
  if (descriptor.kind === 'friendly-graveyard-minion' && cardTargetCandidates(match, seat, card).length === 0) {
    return 'no valid fallen minion';
  }
  if (!target) return 'target required';
  if (targetMatchesDescriptor(match, seat, descriptor, target)) return null;
  if (target.kind === 'minion') {
    const hit = findMinion(match, target.instanceId);
    if (hit && hit.seat !== seat && hit.minion.keywords.includes('stealth') && !hit.minion.stealthBroken) {
      return 'cannot target a Stealth minion';
    }
  }
  return descriptor.kind === 'friendly-graveyard-minion'
    ? 'invalid or stale graveyard target'
    : 'invalid target';
}

/**
 * The spendable mana a player would have this turn immediately after playing a
 * `gain-mana` card (the Coin) from `currentMana`: pay the card's cost, gain its
 * temporary amount. The **one** definition of the boosted-mana arithmetic - the
 * engine's auto-skip probe ({@link hasLegalCardAction}) and the greedy AI's
 * Coin policy (`isWorthPlaying` / `greedyPlayValue` in {@link ./cardAi}) must
 * agree on when the Coin "unlocks" a play, so both call this instead of
 * restating the formula.
 */
export function manaAfterGainMana(currentMana: number, card: CardInstance): number {
  const gained = card.effect?.kind === 'gain-mana' ? card.effect.amount : 0;
  return currentMana - card.cost + gained;
}

/**
 * Whether `seat` can legally play a card or attack right now.
 *
 * Ending the turn and conceding are deliberately excluded: callers use this to
 * detect turns where the player has no gameplay decision to make. Spell
 * legality stays behind the same target validator used by {@link playCard}, so
 * auto-driving callers cannot drift from the battle rules.
 */
export function hasLegalCardAction(match: CardMatch, seat: Seat): boolean {
  if (match.phase !== 'playing' || match.turn !== seat) return false;
  const player = match.players[seat];
  const foe = opponentOf(seat);

  const readyAttacker = player.board.some((minion) => minion.canAttack && minion.attack > 0);
  if (readyAttacker) {
    const taunts = match.players[foe].board.filter((minion) => minion.keywords.includes('taunt'));
    if (
      taunts.length === 0
      || taunts.some((minion) => !minion.keywords.includes('stealth') || minion.stealthBroken)
    ) {
      return true;
    }
  }

  const playableAt = (card: CardInstance, mana: number): boolean => {
    if (card.cost > mana) return false;
    if (card.kind === 'monster' && !hasOpenBoardSlot(match, seat)) return false;
    if (card.effect?.kind === 'summon' && card.effect.summonDelayTurns && !hasOpenBoardSlot(match, seat)) return false;
    const descriptor = cardTargetDescriptor(card);
    return descriptor.kind === 'none' || cardTargetCandidates(match, seat, card).length > 0;
  };
  return player.hand.some((card) => {
    // The Coin (`gain-mana`) is technically always playable, but playing it
    // with nothing to spend the mana on is never a gameplay decision - a hand
    // holding only the Coin must still auto-skip, and the Coin must never
    // count as "a legal action" unless it unlocks one.
    if (card.effect?.kind === 'gain-mana') {
      if (card.cost > player.mana) return false;
      const boosted = manaAfterGainMana(player.mana, card);
      return player.hand.some((other) =>
        other !== card && other.effect?.kind !== 'gain-mana' && playableAt(other, boosted));
    }
    return playableAt(card, player.mana);
  });
}

/** Apply a validated spell's effect. */
function resolveAction(
  match: CardMatch,
  seat: Seat,
  card: CardInstance,
  target: CardTarget | undefined,
  effects: CardEffect[],
): void {
  const effect = card.effect!;
  const p = match.players[seat];
  const foe = opponentOf(seat);
  switch (effect.kind) {
    case 'heal-hero': {
      const healed = heroHealAmount(p, effect.amount);
      p.heroHealth += healed;
      effects.push({ kind: 'heal', target: { kind: 'hero', seat }, amount: healed });
      return;
    }
    case 'heal-friendly': {
      const t = target!;
      if (t.kind === 'hero') {
        const healed = heroHealAmount(p, effect.amount);
        p.heroHealth += healed;
        effects.push({ kind: 'heal', target: t, amount: healed });
      } else {
        const minion = findMinion(match, t.instanceId)!.minion;
        const before = minion.health;
        minion.health = Math.min(minion.maxHealth, minion.health + effect.amount);
        effects.push({ kind: 'heal', target: t, amount: minion.health - before });
      }
      return;
    }
    case 'damage-any': {
      damageSpellTarget(match, seat, target!, effect.amount, effects);
      return;
    }
    case 'damage-all-enemies': {
      const targets = [...match.players[foe].board];
      if (effect.split && targets.length > 0) {
        // Explosion Potion (`split`) spends a fixed damage budget to kill as many
        // enemy minions as possible rather than dividing it evenly (an even split
        // leaves both a stout and a frail minion alive when either could have been
        // cleared). Greedily lethal the cheapest-to-kill minions first, then dump
        // any remainder onto the next survivor. A minion still holding unspent
        // Protection absorbs a whole packet, so it cannot be killed by this effect
        // (kill-cost is unreachable) and sorts last. Deterministic (ties break by
        // board order) and the whole budget is preserved.
        const killCost = (m: CardInstance): number =>
          m.keywords.includes('protection') && !m.protectionSpent ? Infinity : m.health;
        const order = targets
          .map((minion, index) => ({ minion, index }))
          .sort((a, b) => killCost(a.minion) - killCost(b.minion) || a.index - b.index);
        const alloc = new Array<number>(targets.length).fill(0);
        let budget = effect.amount;
        let leftoverIndex = -1;
        for (const { minion, index } of order) {
          if (budget <= 0) break;
          const cost = killCost(minion);
          if (Number.isFinite(cost) && budget >= cost) {
            alloc[index] = cost;
            budget -= cost;
          } else {
            leftoverIndex = index; // cheapest survivor takes the remaining damage
            break;
          }
        }
        if (budget > 0 && leftoverIndex >= 0) alloc[leftoverIndex] = budget;
        for (let i = 0; i < targets.length; i++) {
          if (alloc[i]! > 0) damageMinion(match, targets[i]!, alloc[i]!, effects);
        }
      } else {
        for (const target of targets) damageMinion(match, target, effect.amount, effects);
      }
      reap(match, effects);
      return;
    }
    case 'damage-all-minions': {
      for (const side of match.players) {
        for (const m of side.board) damageMinion(match, m, effect.amount, effects);
      }
      reap(match, effects);
      return;
    }
    case 'damage-chain': {
      const primary = target!;
      const secondaryAmount = effect.secondaryAmount ?? effect.amount;
      const maxTargets = Math.max(1, effect.maxTargets ?? 1);
      let minionDamaged = false;
      if (primary.kind === 'hero') {
        damageSpellTarget(match, seat, primary, effect.amount, effects);
      } else {
        const hit = findMinion(match, primary.instanceId)!;
        damageMinion(match, hit.minion, effect.amount, effects);
        minionDamaged = true;
      }
      if (match.phase === 'ended') return;

      const secondaryTargets: CardTarget[] = match.players[foe].board
        .filter((minion) => primary.kind !== 'minion' || minion.instanceId !== primary.instanceId)
        .map((minion) => ({ kind: 'minion', instanceId: minion.instanceId }));
      if (primary.kind !== 'hero') secondaryTargets.push({ kind: 'hero', seat: foe });
      for (const chained of secondaryTargets.slice(0, maxTargets - 1)) {
        if (chained.kind === 'hero') {
          damageHero(match, chained.seat, secondaryAmount, 'spell', effects);
        } else {
          const hit = findMinion(match, chained.instanceId);
          if (!hit) continue;
          damageMinion(match, hit.minion, secondaryAmount, effects);
          minionDamaged = true;
        }
      }
      if (minionDamaged) reap(match, effects);
      return;
    }
    case 'return-friendly': {
      const t = target!;
      const hit = findMinion(match, t.kind === 'minion' ? t.instanceId : -1)!;
      const owner = match.players[hit.seat];
      owner.board = owner.board.filter((minion) => minion.instanceId !== hit.minion.instanceId);
      const def = cardDef(hit.minion.cardId, hit.minion.rarity);
      if (def.kind !== 'monster') return;
      const returned: CardInstance = {
        instanceId: hit.minion.instanceId,
        cardId: def.cardId,
        name: def.name,
        rarity: def.rarity,
        kind: 'monster',
        cost: def.cost,
        attack: def.attack,
        health: def.health,
        maxHealth: def.health,
        keywords: def.keywords,
        slayerGroup: def.slayerGroup,
        abilities: def.abilities,
        attacksRemaining: 0,
        protectionSpent: false,
        stealthBroken: false,
        paralyzed: false,
        canAttack: false,
        summonedThisTurn: false,
      };
      owner.hand.push(returned);
      effects.push({
        kind: 'return-to-hand',
        seat: hit.seat,
        instanceId: returned.instanceId,
        cardId: returned.cardId,
      });
      return;
    }
    case 'draw': {
      for (let i = 0; i < effect.amount; i++) drawCard(match, seat, effects);
      return;
    }
    case 'resurrect': {
      resolveScriptedEffect(match, seat, card, { kind: 'resurrect' }, effects, target);
      return;
    }
    case 'break-paralyze': {
      const t = target!;
      const hit = findMinion(match, t.kind === 'minion' ? t.instanceId : -1)!;
      damageMinion(match, hit.minion, effect.amount, effects);
      reap(match, effects);
      return;
    }
    case 'paralyze': {
      const t = target!;
      const hit = findMinion(match, t.kind === 'minion' ? t.instanceId : -1)!;
      hit.minion.paralyzed = true;
      hit.minion.canAttack = false;
      hit.minion.attacksRemaining = 0;
      effects.push({
        kind: 'status',
        action: 'apply',
        status: 'paralyze',
        seat: hit.seat,
        instanceId: hit.minion.instanceId,
      });
      return;
    }
    case 'delayed-damage-any': {
      const pending: PendingDelayedDamage = {
        sourceSeat: seat,
        target: target!,
        amount: effect.amount,
        resolveAtTurnNumber: match.turnNumber + 2,
      };
      match.pendingDelayedDamage.push(pending);
      effects.push({
        kind: 'delayed-damage',
        action: 'arm',
        sourceSeat: seat,
        target: pending.target,
        amount: pending.amount,
      });
      return;
    }
    case 'animate-dead': {
      const corpseIndex = target?.kind === 'graveyard'
        ? (p.diedMinions ?? []).findIndex((corpse) => corpse.instanceId === target.instanceId)
        : -1;
      const corpse = corpseIndex >= 0 ? p.diedMinions?.splice(corpseIndex, 1)[0] : undefined;
      if (!corpse || !effect.cardId) return;
      resolveScriptedEffect(
        match,
        seat,
        card,
        { kind: 'summon', cardId: effect.cardId, count: effect.amount },
        effects,
      );
      return;
    }
    case 'summon': {
      const cardId = effect.cardIds?.length
        ? effect.cardIds[hasOpenBoardSlot(match, seat)
          ? nextRandomIndex(match, effect.cardIds.length)
          : 0]
        : effect.cardId;
      if (!cardId) return;
      if (effect.summonDelayTurns) {
        const count = Math.min(effect.amount, openBoardSlots(match, seat));
        const pending: PendingSummon = {
          seat,
          sourceInstanceId: card.instanceId,
          cardId,
          rarity: card.rarity,
          count,
          resolveAtTurnNumber: match.turnNumber + effect.summonDelayTurns * 2,
        };
        match.pendingSummons.push(pending);
        effects.push({
          kind: 'ability',
          seat,
          instanceId: card.instanceId,
          text: `${card.name} will summon ${cardDef(cardId, card.rarity).name} at the start of the caster's next turn.`,
        });
        return;
      }
      resolveScriptedEffect(match, seat, card, { kind: 'summon', cardId, count: effect.amount }, effects);
      return;
    }
    case 'reactive-armor': {
      p.reactiveArmor = effect.amount;
      effects.push({ kind: 'ward', action: 'gain', seat, ward: 'reactive-armor', amount: effect.amount });
      return;
    }
    case 'magic-reflection': {
      p.magicReflection = true;
      effects.push({ kind: 'ward', action: 'gain', seat, ward: 'magic-reflection' });
      return;
    }
    case 'buff-friendly': {
      const t = target!;
      const hit = findMinion(match, t.kind === 'minion' ? t.instanceId : -1)!;
      hit.minion.attack += effect.amount;
      hit.minion.health += effect.buffHealth ?? 0;
      hit.minion.maxHealth += effect.buffHealth ?? 0;
      effects.push({ kind: 'buff', instanceId: hit.minion.instanceId, attack: effect.amount, health: effect.buffHealth ?? 0 });
      return;
    }
    case 'gain-mana': {
      // Temporary mana for this turn only: raises the spendable pool (which may
      // now exceed the crystal count), never `maxMana`; beginTurn's refill to
      // exactly `maxMana` is what expires any unspent remainder.
      p.mana += effect.amount;
      return;
    }
    case 'blood-oath': {
      match.bloodOathExpiresAtTurnNumber = match.turnNumber + 2;
      effects.push({
        kind: 'ability',
        seat,
        instanceId: card.instanceId,
        text: 'Blood Oath links both heroes until the start of the caster\'s next turn.',
      });
      return;
    }
    case 'heal-all-friendly': {
      let restored = 0;
      for (const minion of p.board) {
        const amount = Math.max(0, minion.maxHealth - minion.health);
        if (amount === 0) continue;
        minion.health += amount;
        restored += amount;
        effects.push({ kind: 'heal', target: { kind: 'minion', instanceId: minion.instanceId }, amount });
      }
      if (restored > 0) damageHero(match, seat, Math.ceil(restored / 2), 'ability', effects);
      return;
    }
    case 'destroy-minion': {
      const t = target!;
      const hit = findMinion(match, t.kind === 'minion' ? t.instanceId : -1);
      if (!hit) return;
      hit.minion.health = 0;
      reap(match, effects);
      return;
    }
    case 'grant-fury': {
      const t = target!;
      const hit = findMinion(match, t.kind === 'minion' ? t.instanceId : -1);
      if (!hit) return;
      const minion = hit.minion;
      minion.temporaryFury = true;
      if (!minion.summonedThisTurn && !minion.paralyzed && !minion.keywords.includes('fury')) {
        minion.attacksRemaining = Math.min(2, (minion.attacksRemaining ?? 0) + 1);
        minion.canAttack = (minion.attacksRemaining ?? 0) > 0;
      }
      effects.push({ kind: 'ability', seat, instanceId: minion.instanceId, text: `${minion.name} gains Fury this turn.` });
      return;
    }
  }
}

function attack(match: CardMatch, cmd: Extract<CardCommand, { kind: 'Attack' }>): CardCommandResult {
  if (cmd.seat !== match.turn) return reject('not your turn');
  const p = match.players[cmd.seat];
  const foe = opponentOf(cmd.seat);
  const attacker = p.board.find((m) => m.instanceId === cmd.attackerId);
  if (!attacker) return reject('no such attacker');
  if (!attacker.canAttack) return reject('minion cannot attack');
  if (attacker.attack <= 0) return reject('minion has no attack');
  if (cmd.target.kind === 'graveyard') return reject('cannot attack a graveyard target');

  // Taunt gate: if the defender has any taunt minions, only they are legal targets.
  const taunts = match.players[foe].board.filter((m) => m.keywords.includes('taunt'));
  if (cmd.target.kind === 'hero') {
    if (cmd.target.seat !== foe) return reject('can only attack the enemy hero');
    if (taunts.length > 0) return reject('must attack a Taunt minion first');
  } else {
    const hit = findMinion(match, cmd.target.instanceId);
    if (!hit || hit.seat !== foe) return reject('must target an enemy minion');
    if (hit.minion.keywords.includes('stealth') && !hit.minion.stealthBroken) return reject('cannot target a Stealth minion');
    if (taunts.length > 0 && !hit.minion.keywords.includes('taunt')) {
      return reject('must attack a Taunt minion first');
    }
  }

  const effects: CardEffect[] = [];
  p.consecutiveTimeouts = 0;
  attacker.attacksRemaining = Math.max(0, (attacker.attacksRemaining ?? 1) - 1);
  attacker.canAttack = attacker.attacksRemaining > 0;
  if (attacker.keywords.includes('stealth')) attacker.stealthBroken = true;
  effects.push({ kind: 'attack', attackerId: attacker.instanceId, target: cmd.target });

  if (cmd.target.kind === 'hero') {
    const dealt = damageHero(match, foe, attacker.attack, 'attack', effects);
    if (attacker.keywords.includes('drain')) healHeroFromDrain(match, cmd.seat, dealt, effects);
  } else {
    const defender = findMinion(match, cmd.target.instanceId)!.minion;
    const before = defender.health;
    const dealt = damageMinion(match, defender, attacker.attack, effects);
    if (attacker.keywords.includes('poison') && dealt > 0) defender.health = 0;
    if (defender.attack > 0 && !attacker.keywords.includes('ranged')) {
      const returned = damageMinion(match, attacker, defender.attack, effects);
      if (defender.keywords.includes('poison') && returned > 0) attacker.health = 0;
      if (defender.keywords.includes('drain')) healHeroFromDrain(match, foe, returned, effects);
    }
    if (attacker.keywords.includes('drain')) healHeroFromDrain(match, cmd.seat, dealt, effects);
    if (attacker.keywords.includes('overwhelm') && defender.health <= 0) damageHero(match, foe, Math.max(0, attacker.attack - before), 'attack', effects);
    reap(match, effects);
  }
  return { ok: true, effects };
}

function endTurn(match: CardMatch, seat: Seat, timedOut: boolean): CardCommandResult {
  if (seat !== match.turn) return reject('not your turn');
  const p = match.players[seat];
  const effects: CardEffect[] = [];

  if (timedOut) {
    p.consecutiveTimeouts += 1;
    if (p.consecutiveTimeouts >= TIMEOUTS_TO_FORFEIT) {
      settle(match, opponentOf(seat), 'forfeit', effects);
      return { ok: true, effects };
    }
  } else {
    p.consecutiveTimeouts = 0;
  }

  for (const minion of [...p.board]) {
    const heal = minion.abilities?.heal ?? (minion.keywords.includes('heal') ? 1 : 0);
    if (heal > 0) {
      const amount = Math.min(heal, minion.maxHealth - minion.health);
      minion.health += amount; effects.push({ kind: 'heal', target: { kind: 'minion', instanceId: minion.instanceId }, amount });
    }
    if (minion.abilities?.endOfTurn) resolveScriptedEffect(match, seat, minion, minion.abilities.endOfTurn, effects);
  }

  for (const minion of p.board) {
    if (minion.paralyzed) clearParalyze(match, minion, effects);
    minion.temporaryFury = undefined;
  }

  if (match.phase === 'ended') return { ok: true, effects };

  beginTurn(match, opponentOf(seat), effects);
  return { ok: true, effects };
}

function concede(match: CardMatch, seat: Seat): CardCommandResult {
  const effects: CardEffect[] = [];
  settle(match, opponentOf(seat), 'concede', effects);
  return { ok: true, effects };
}

// ---------------------------------------------------------------------------
// Read-only helpers for callers (the AI, the UI, the tests).
// ---------------------------------------------------------------------------

/** The seat whose turn it is. */
export function activeSeat(match: CardMatch): Seat {
  return match.turn;
}

/** Whether the match is over. */
export function isOver(match: CardMatch): boolean {
  return match.phase === 'ended';
}
